import { useMemo } from "react";

import { useIdentities, useLookups, useOktaAccess, useOktaSync } from "src/api/hooks";
import { ReadFailure } from "src/components/read-failure";
import {
  Badge,
  Button,
  Card,
  Empty,
  Field,
  Input,
  Select,
  Table,
  humanPopulation,
  humanStatus,
  statusTone
} from "src/components/ui";
import type { IdentityFilter, Population } from "src/graphql/generated";
import { describeEngagementHistory, summariseEngagements } from "src/lib/engagement-history";
import {
  type FilterControls,
  describeActiveFilters,
  describeFilterCombination,
  describeStatusScope,
  filterLabels,
  statusFilters
} from "src/lib/identity-filters";
import { personDisplayName } from "src/lib/names";
import { type OktaAccess, describeOktaAccess } from "src/lib/okta-access";
import { type OktaSync, describeOktaSync } from "src/lib/okta-sync";
import {
  type PopulationFilterChoice,
  type Viewer,
  populationFilterChoice
} from "src/screens/types";

// Request one extra row to know whether Next exists, including at exact page
// boundaries. Offset advances by the displayed page size, not the lookahead row.
const pageSize = 50;

export type PeopleView = FilterControls & { offset: number };

export const INITIAL_PEOPLE_VIEW: PeopleView = {
  population: "",
  status: "",
  search: "",
  currentOnly: false,
  notClosedOut: false,
  offset: 0
};

/**
 * The People list, and the control that opens the create form.
 *
 * The control is here; the panel it opens is rendered by `App`, deliberately. The
 * form has to survive opening somebody from this table — referencing another person
 * is the whole reason it floats — and a panel mounted here would unmount with this
 * screen the moment a row was clicked, taking the draft with it.
 */
export function Identities({
  viewer,
  view,
  onView,
  onOpen,
  onAddPerson
}: {
  viewer: Viewer;
  view: PeopleView;
  onView: (next: PeopleView) => void;
  onOpen: (id: string) => void;
  onAddPerson: () => void;
}) {
  const { population, status, search, currentOnly, notClosedOut, offset } = view;
  // Filter and page change together so no request uses a new filter at an old
  // offset. App owns this view to preserve it when returning from a person.
  const changeFilters = (next: Partial<FilterControls>) => onView({ ...view, ...next, offset: 0 });
  const canCreate = viewer.writablePopulations.length > 0;

  // One object for the five controls, so what is on screen and what is sent are read
  // off the same value rather than assembled twice.
  const controls: FilterControls = { population, status, search, currentOnly, notClosedOut };
  const activeFilters = describeActiveFilters(controls);
  const combination = describeFilterCombination(controls);
  const statusScope = describeStatusScope(controls);

  const filter: IdentityFilter = useMemo(
    () => ({
      populations: population ? [population] : undefined,
      // Ended *and* still current is the offboarding worklist: the term is over by
      // its dates and nobody recorded a decision to end it. The two existing filter
      // primitives compose to say that exactly, so it needs no new API.
      statuses: notClosedOut ? ["ENDED"] : statusFilters.find((f) => f.value === status)?.statuses,
      currentOnly: currentOnly || notClosedOut || undefined,
      // No start-date cutoff any more. This once carried the onboarding worklist —
      // people a stored status had left behind because nobody flipped it on their
      // first day. Status is derived from the dates now, so that list can have no
      // members and the query that found them is gone with it.
      startsOnOrBefore: undefined,
      search: search.trim() || undefined,
      limit: pageSize + 1,
      offset
    }),
    [population, status, search, currentOnly, notClosedOut, offset]
  );

  const { data, isPending, isFetching, error, refetch } = useIdentities(filter);

  // The column shows the country's name; the row carries its ISO 3166-1 code. The
  // names come from the same backend list the pickers use, so a code renders here
  // exactly as it reads on the person's own page. Until the lookups land — or for a
  // code the list does not know — the code itself is shown rather than a dash, which
  // would read as "not recorded".
  const { data: lookups } = useLookups();
  const countryNames = useMemo(
    () => new Map((lookups?.countries ?? []).map((c) => [c.code, c.name])),
    [lookups]
  );

  const hasNext = (data?.identities.length ?? 0) > pageSize;
  const rows = data?.identities.slice(0, pageSize) ?? [];
  const paginated = offset > 0 || hasNext;

  const showOkta = viewer.oktaExportEnabled;
  const okta = useOktaAccess(
    rows.map((identity) => identity.id),
    showOkta
  );
  const oktaById = useMemo(
    () => new Map((okta.data?.oktaAccess ?? []).map((access) => [access.identityId, access])),
    [okta.data]
  );
  // Writers only: whether Okta should hold someone depends on terms in every
  // population, which the server answers only for a caller who can end terms.
  const showSync = showOkta && viewer.writablePopulations.length > 0;
  const sync = useOktaSync(
    rows.map((identity) => identity.id),
    showSync
  );
  const syncById = useMemo(
    () => new Map((sync.data?.oktaSync ?? []).map((status) => [status.identityId, status])),
    [sync.data]
  );

  return (
    <div className="flex flex-col gap-4">
      <Card title="Filters">
        <div className="flex flex-wrap items-start gap-3">
          <PopulationFilter
            choice={populationFilterChoice(viewer)}
            value={population}
            onChange={(population) => changeFilters({ population })}
          />

          {/* The hint, because one option covers two badges: somebody who filters to
              Hired reads rows badged "No start date" and "Starts later", and without
              it the filter looks broken rather than deliberate. */}
          <Field label="Status" hint="Hired means signed but not working yet">
            <Select
              value={status}
              disabled={notClosedOut}
              onChange={(e) => changeFilters({ status: e.target.value })}
            >
              <option value="">Any</option>
              {statusFilters.map((f) => (
                <option key={f.value} value={f.value}>
                  {f.label}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Search" hint="Names and emails">
            <Input
              value={search}
              placeholder="name or email"
              onChange={(e) => changeFilters({ search: e.target.value })}
            />
          </Field>

          {/* No natural label sits above a checkbox pair, but every neighbour here
              is a labelled field (label text, then gap-1, then the control), so
              top-aligning this group as-is would line its checkboxes up with the
              *label text* of Population/Status/Search rather than with their
              *inputs*. This invisible spacer reserves the same label-line height
              so the checkboxes land flush with the selects and the search box. */}
          <div className="flex flex-col gap-1 text-sm">
            <span aria-hidden="true" className="invisible font-medium">
              Options
            </span>
            {/* Kept side by side, as they were: the ask was to align the row, not
                to restack it. flex-wrap lets them break onto a second line on a
                narrow viewport without splitting the group up. */}
            <div className="flex flex-wrap items-center gap-4">
              {/* Ticked, not merely disabled, while the box beside it is asking: that
                  one sends this condition too, so the wider question really is on.
                  Under the old label an unchecked box here was only quietly wrong;
                  under one that states its own predicate it would show the filter off
                  while the list on screen is narrowed by exactly it. */}
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={currentOnly || notClosedOut}
                  disabled={notClosedOut}
                  onChange={(e) => changeFilters({ currentOnly: e.target.checked })}
                />
                {filterLabels.currentOnly}
              </label>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={notClosedOut}
                  onChange={(e) => changeFilters({ notClosedOut: e.target.checked })}
                />
                {filterLabels.notClosedOut}
              </label>
            </div>
          </div>
        </div>

        {/* What the controls above currently add up to, said out loud.

            A tick is invisible the moment a reader stops looking at it, and neither
            the Status select nor "Not closed out" clears or disables the other.
            So the combination that produced the list on screen was, until now,
            reconstructable only by scrolling back up and re-reading five controls —
            which is how a leftover tick got a tester a straight answer to a question
            they had not asked, and a bug report saying the filters did not work.

            Only the *contents* are conditional — a permanent "no filters" line would
            be noise on the common case, and an unfiltered list needs no explanation.
            The live region itself is always mounted, which is the part that is easy to
            get wrong: a region added to the DOM in the same render that gives it
            content is not announced by screen readers that only watch regions present
            before the mutation, so a conditional wrapper would have been silent on the
            first filter — the one that matters. The border and padding sit on the
            inner block, so an empty region draws nothing. */}
        <div aria-live="polite">
          {activeFilters.length > 0 ? (
            <div className="mt-3 border-t border-slate-200 pt-3 text-xs text-slate-600">
              <p>
                <span className="font-medium text-slate-700">Filtering by:</span>{" "}
                {activeFilters.join(" · ")}
              </p>
              {/* Amber, matching the console's other "this is not what it looks like"
                  notices, because a narrower-than-it-reads combination is the one part
                  of this the reader has to act on. */}
              {combination ? <p className="mt-1 text-amber-800">{combination}</p> : null}
              {/* The same colour for the same kind of fact: the Status filter is asked
                  of a person's whole history while the column beside it shows where they
                  stand now, so an Ended search can return a row badged Active. That used
                  to be legible from the row, which printed a chip per term; with one
                  entry per population the match is carried by the count of earlier terms,
                  and this is what says so. */}
              {statusScope ? <p className="mt-1 text-amber-800">{statusScope}</p> : null}
            </div>
          ) : null}
        </div>
      </Card>

      <Card
        title={`People (${rows.length}${paginated ? " on this page" : ""})`}
        actions={
          // Only offered to somebody who can write somewhere. The backend refuses a
          // create from a reader whatever the browser shows; this is about not
          // inviting them into a form that will turn them away.
          //
          // Kept rendered while the panel is open rather than hidden, so closing the
          // panel has somewhere to put focus back.
          canCreate ? (
            <Button data-add-person variant="secondary" onClick={onAddPerson}>
              Add a person
            </Button>
          ) : null
        }
      >
        {error ? <ReadFailure error={error} /> : null}
        {isPending ? <Empty>Loading…</Empty> : null}
        {/* Two different facts, and the old message conflated them. "Nobody matches
            those filters." names no filter, so it reads as a statement about the
            data — there is nobody like that — when what it usually means is that a
            reader is holding a narrower question than they think. Naming the filters
            here is the same fix the organizations roster already carries, which says
            which Show setting is hiding rows rather than letting a short list be read
            as a complete one.

            No "and All would list N" clause to go with it, unlike that roster: it
            filters client-side and so knows the unfiltered count, while this one is
            filtered in the query and a second unfiltered read to produce the number
            would be a real cost for a sentence. What is said instead is which filters
            are in play, which is the actionable half. */}
        {!isPending && !error && rows.length === 0 ? (
          <Empty>
            {offset > 0
              ? "No people remain on this page. Go to the previous page or change the filters."
              : activeFilters.length === 0
                ? // No filter is set, so the emptiness is the answer. Careful not to
                  // claim the database is empty: a reader sees only their own
                  // populations, and absent and out-of-scope look the same from here.
                  "No people yet, or none you can read."
                : `Nobody matches ${activeFilters.join(" · ")}. Clear one to widen the search.`}
          </Empty>
        ) : null}

        {showOkta && okta.error ? (
          <p className="mb-2 text-xs text-amber-800">
            Okta access could not be read: {okta.error.message}{" "}
            <button
              type="button"
              className="underline disabled:opacity-50"
              disabled={okta.isFetching}
              onClick={() => okta.refetch()}
            >
              Try again
            </button>
          </p>
        ) : null}
        {showSync && sync.error ? (
          <p className="mb-2 text-xs text-amber-800">
            Okta sync status could not be read: {sync.error.message}{" "}
            <button
              type="button"
              className="underline disabled:opacity-50"
              disabled={sync.isFetching}
              onClick={() => sync.refetch()}
            >
              Try again
            </button>
          </p>
        ) : null}
        {showOkta && !okta.error && rows.length > 0 ? (
          <p className="mb-2 text-xs text-slate-500">
            {okta.isFetching
              ? "Checking Okta…"
              : okta.dataUpdatedAt
                ? `Okta access checked at ${new Date(okta.dataUpdatedAt).toLocaleTimeString()}.`
                : null}{" "}
            <button
              type="button"
              className="underline disabled:opacity-50"
              disabled={okta.isFetching}
              onClick={() => {
                okta.refetch();
                if (showSync) {
                  sync.refetch();
                }
              }}
            >
              Check again
            </button>
          </p>
        ) : null}

        {rows.length > 0 ? (
          <Table
            head={[
              "Name",
              "Emails",
              "Engagements",
              "Work country",
              "System access",
              ...(showOkta ? ["Okta access"] : []),
              ...(showSync ? ["Okta sync"] : [])
            ]}
          >
            {rows.map((identity) => (
              <tr
                key={identity.id}
                className="cursor-pointer border-b border-slate-100 hover:bg-slate-50"
                onClick={() => onOpen(identity.id)}
              >
                <td className="px-2 py-2">
                  {/* The name is a real control, and the row keeps its click: a <tr>
                      with an onClick is reachable by no keyboard and announced as
                      nothing, and this is the only way into a person's record —
                      while anywhere-in-the-row is how a dense table gets used with a
                      mouse. The organization roster already does exactly this.
                      stopPropagation so one press is one call.

                      Nothing but a focus ring on it, deliberately: the row's hover is
                      the affordance a mouse already has, and underlining every name
                      would draw a line down the busiest column in the console. */}
                  <button
                    type="button"
                    className="-mx-1 block rounded px-1 text-left font-medium text-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-500"
                    onClick={(event) => {
                      event.stopPropagation();
                      onOpen(identity.id);
                    }}
                  >
                    {personDisplayName(identity)}
                  </button>
                  {identity.legalFullNameLocal ? (
                    <div className="text-xs text-slate-500">{identity.legalFullNameLocal}</div>
                  ) : null}
                </td>
                <td className="px-2 py-2 text-slate-600">
                  <div>{identity.workEmail ?? "—"}</div>
                  {identity.personalEmail ? (
                    <div className="text-xs text-slate-500">{identity.personalEmail}</div>
                  ) : null}
                </td>
                {/* One entry per population, not one per engagement. A person rehired
                  twice holds three Partner terms, and a chip each read as "Ended
                  Partner, Ended Partner, Active Partner" — three chips with no date on
                  any of them, which is what duplicated data looks like even when it is
                  ordinary history. What stands here instead is where the person is now
                  in that population, the days that term covered, and a count of the
                  terms behind it; the row opens the person, whose own page is where
                  those are read one by one. The two populations still get an entry
                  each — holding a Partner term and an On-demand contractor term at once
                  is deliberate, and collapsing those together would hide a real fact. */}
                <td className="px-2 py-2">
                  <div className="flex flex-col gap-1">
                    {summariseEngagements(identity.engagements).map((entry) => {
                      const caption = describeEngagementHistory(entry);
                      return (
                        <div key={entry.population}>
                          <span className="flex items-center gap-1">
                            <Badge tone={statusTone(entry.term.status)}>
                              {humanStatus(entry.term.status)}
                            </Badge>
                            <span className="text-xs text-slate-500">
                              {humanPopulation(entry.population)}
                            </span>
                            {/* The signal that was being fetched and thrown away, and
                              the one that explains the row a reader is most likely to
                              file a bug about: a term whose end date has passed with
                              nobody closing it out reads "Ended" here and is still the
                              term the filter above returns, so without this the two
                              look like a contradiction. The person's own page has
                              flagged it in amber since INFRA-624; this is the same
                              phrase, colour and weight, because it is the same fact —
                              and since INFRA-695 the filter that returns the row is
                              spelled the same way too, so the pairing reads as one
                              sentence rather than as two claims to reconcile.

                              The collapse cannot swallow it: a term nobody closed out
                              is a term nobody terminated, so it is always the one
                              standing for its population. summariseEngagements' own
                              doc argues that, and its tests pin it. */}
                            {entry.term.hasLapsed ? (
                              <span className="text-xs font-medium text-amber-700">
                                not closed out
                              </span>
                            ) : null}
                          </span>
                          {entry.term.department ? (
                            <span className="block text-xs text-slate-500">
                              Department: {entry.term.department.name}
                            </span>
                          ) : null}
                          {caption ? (
                            <span className="block text-xs text-slate-500">{caption}</span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </td>
                <td className="px-2 py-2 text-slate-600">
                  {identity.workLocation
                    ? (countryNames.get(identity.workLocation) ?? identity.workLocation)
                    : "—"}
                </td>
                {/* This service's record of whether they may have access, beside
                  Okta's own answer when Okta is configured: the two differ while a
                  removal or restore is on its way there. */}
                <td className="whitespace-nowrap px-2 py-2">
                  {identity.hasSystemAccess ? (
                    <Badge tone="green">Granted</Badge>
                  ) : (
                    <Badge tone="slate">Not granted</Badge>
                  )}
                </td>
                {showOkta ? (
                  <td className="whitespace-nowrap px-2 py-2">
                    <OktaAccessCell
                      access={oktaById.get(identity.id)}
                      checking={okta.isPending && okta.isFetching}
                    />
                  </td>
                ) : null}
                {showSync ? (
                  <td className="whitespace-nowrap px-2 py-2">
                    <OktaSyncCell
                      sync={syncById.get(identity.id)}
                      checking={sync.isPending && sync.isFetching}
                    />
                  </td>
                ) : null}
              </tr>
            ))}
          </Table>
        ) : null}
        <div
          role="group"
          aria-label="People pagination"
          className="mt-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3"
        >
          <p aria-live="polite" className="text-xs text-slate-600">
            Page {offset / pageSize + 1}
            {isFetching
              ? " · Loading…"
              : error
                ? " · Could not load this page"
                : rows.length > 0
                  ? ` · Showing ${offset + 1}–${offset + rows.length}${hasNext ? "" : " · Last page"}`
                  : " · No results"}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              disabled={offset === 0 || isFetching}
              onClick={() => onView({ ...view, offset: Math.max(0, offset - pageSize) })}
            >
              Previous
            </Button>
            {error ? (
              <Button variant="secondary" disabled={isFetching} onClick={() => refetch()}>
                Retry
              </Button>
            ) : null}
            <Button
              variant="secondary"
              disabled={!hasNext || isFetching || Boolean(error)}
              onClick={() => onView({ ...view, offset: offset + pageSize })}
            >
              Next
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

/**
 * One person's Okta account, read live from Okta. A dash when there is no answer
 * for them, which is also what a failed read leaves: the notice above the table
 * says why once rather than in every row.
 */
function OktaAccessCell({ access, checking }: { access?: OktaAccess; checking: boolean }) {
  if (checking) {
    return <span className="text-xs text-slate-500">Checking…</span>;
  }
  if (!access) {
    return <span className="text-slate-600">—</span>;
  }
  const { label, tone, caption } = describeOktaAccess(access);
  return (
    <div>
      {tone ? <Badge tone={tone}>{label}</Badge> : <span className="text-slate-600">{label}</span>}
      {caption ? <span className="block text-xs text-slate-500">{caption}</span> : null}
    </div>
  );
}

/**
 * Whether Okta holds the latest of one person, from what this service sent. Why a
 * request failed is a hover here and written out on the person's page.
 */
function OktaSyncCell({ sync, checking }: { sync?: OktaSync; checking: boolean }) {
  if (checking) {
    return <span className="text-xs text-slate-500">Checking…</span>;
  }
  if (!sync) {
    return <span className="text-slate-600">—</span>;
  }
  const { label, tone, caption, detail } = describeOktaSync(sync);
  return (
    <div title={detail}>
      {tone ? <Badge tone={tone}>{label}</Badge> : <span className="text-slate-600">{label}</span>}
      {caption ? <span className="block text-xs text-slate-500">{caption}</span> : null}
    </div>
  );
}

/**
 * The Population filter, which is a chooser only for somebody with a choice to make.
 *
 * A caller who reads one population is already looking at exactly that population,
 * so the filter names it rather than offering it: a dropdown whose only real option
 * is the view you already have cannot do anything, and it leaves the reader
 * wondering what the other choices were.
 *
 * The value stays "" in that case rather than being pinned to the population. The
 * backend intersects the filter with the caller's scope, so an unfiltered query
 * already returns that population and nothing else; naming it is accurate, while
 * filtering by it would send a redundant argument that changes no result.
 */
function PopulationFilter({
  choice,
  value,
  onChange
}: {
  choice: PopulationFilterChoice;
  value: Population | "";
  onChange: (population: Population | "") => void;
}) {
  if (choice.kind === "none") {
    return null;
  }

  if (choice.kind === "fixed") {
    return (
      // Deliberately not Field, which wraps its children in a <label>: a label over
      // static text names no control, and a screen reader announces it pointing at
      // nothing. Field's shape by hand instead, as the checkbox group in this same
      // filter row already does.
      <div className="flex flex-col gap-1 text-sm">
        <span className="font-medium text-slate-700">Population</span>
        <span className="py-1.5 text-slate-900">{humanPopulation(choice.population)}</span>
      </div>
    );
  }

  return (
    <Field label="Population">
      <Select value={value} onChange={(e) => onChange(e.target.value as Population | "")}>
        <option value="">All</option>
        {choice.populations.map((p) => (
          <option key={p} value={p}>
            {humanPopulation(p)}
          </option>
        ))}
      </Select>
    </Field>
  );
}
